/**
 * Scan Feedback - Visual flash and native sound bridge
 *
 * Provides feedback when a barcode/QR code is scanned:
 * - Visual flash overlay (works everywhere)
 * - Native sound request via bridge (for iOS/Android native apps)
 *
 * Native apps should implement:
 * - window.WrestlingIQKiosk.playSound(soundType) where soundType is 'scan_success' or 'scan_error'
 */

export function useScanFeedback(store) {
  let flashCallback = null;

  /**
   * Check if running in native app context
   */
  function isNativeApp() {
    return typeof window !== 'undefined' && window.WrestlingIQKiosk;
  }

  /**
   * Request the native app to play a sound
   * @param {string} soundType - 'scan_success' or 'scan_error'
   */
  function requestNativeSound(soundType = 'scan_success') {
    if (!isNativeApp()) {
      if (store?.state?.debug) {
        console.log('Not in native app, skipping native sound request');
      }
      return;
    }

    if (typeof window.WrestlingIQKiosk.playSound === 'function') {
      window.WrestlingIQKiosk.playSound(soundType);
      if (store?.state?.debug) {
        console.log(`Requested native sound: ${soundType}`);
      }
    } else if (store?.state?.debug) {
      console.log('Native app does not support playSound method');
    }
  }

  /**
   * Trigger the visual flash effect
   * @param {string} type - 'success' (green) or 'error' (red)
   */
  function triggerFlash(type = 'success') {
    if (flashCallback) {
      flashCallback(type);
    }
  }

  /**
   * Register a callback to handle flash events
   * Call this from the Vue component that owns the flash overlay
   * @param {function} callback - Function that receives flash type ('success' or 'error')
   */
  function onFlash(callback) {
    flashCallback = callback;
  }

  /**
   * Trigger full scan feedback (visual + sound)
   * @param {string} type - 'success' or 'error'
   */
  function triggerScanFeedback(type = 'success') {
    triggerFlash(type);
    requestNativeSound(type === 'success' ? 'scan_success' : 'scan_error');
  }

  return {
    isNativeApp,
    requestNativeSound,
    triggerFlash,
    triggerScanFeedback,
    onFlash,
  };
}